import { useState } from 'react';
import { Redirect, useLocation, useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  FeatureSubscriptionPaymentCycle,
  FeatureSubscriptionProductCode,
  FeatureSubscriptionType,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
} from 'queries';

import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import { NodeEnvironment } from 'utils/enum';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import LabeledSwitch from './components/labeled-switch';
import PricingTable from './components/pricing-table';
import PricingTableFooter from './components/pricing-table-footer';

type FeatureValue = boolean | number | string;

export type Plan = {
  id: string;
  name: string;
  isMostPopular: boolean;
  features: Record<string, FeatureValue>;
  priceIdKey: string;
};

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: '1500px',
    margin: 'auto',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
  },
  pricingTableFooterContainer: {
    marginTop: '60px',
  },
  switchContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '60px',
    marginBottom: '60px',
  },
}));

const plans: Plan[] = [
  // {
  //   id: 'trial',
  //   name: 'Trial',
  //   isMostPopular: false,
  //   priceIdKey: FeatureSubscriptionProductCode.TrialPlan,
  //   features: {
  //     'Run Trusuite (real property, liability, and employment discovery) search': true,
  //     'Prepare a deed': true,
  //     'Draft estate planning documents': true,
  //     'Run bank account search - Top 10': false,
  //     'Run a life insurance discovery search': false,
  //     'NEW! Run a stock search - ComputerShare': false,
  //     'Notify credit bureaus and SSA of death': false,
  //     'Prepare IRS Form 56 and file it': false,
  //     'Close out or transfer a bank account': false,
  //     'Close out or transfer an investment account': false,
  //     'Close out or transfer outright stock': false,
  //     'Prepare assignment of business interest/etc (funding)': false,
  //     'Run a retirement account search': false,
  //     'NEW! Life Insurance Quick Search': false,
  //     'Notify employer and request unpaid benefits/survivor benefits': false,
  //     'Open estate/trust checking account': false,
  //     'Probate documents': false,
  //     'Obtain a deed for real property owned by the decedent': false,
  //     'Deed Tax Docs': false,
  //     'Search unclaimed property records nationwide': false,
  //     'Run bank account search - Private Wealth Management Firms': false,
  //     'Request life insurance Form 712': false,
  //     'NEW! Run Bank Search - Quick Results': false,
  //     'NEW! Run a stock search - EQ Shareowner Services - Quick Results': false,
  //     'Run bank account search - Regional Banks - West': false,
  //     'Run bank account search - Regional Banks - SE/SW': false,
  //     'Run bank account search - Regional Banks - NE/Mid Atlantic': false,
  //     'Run bank account search - Regional Banks - Midwest': false,
  //     'Obtain more information on a known or found account': false,
  //     'Request the date of death value for each asset and/or liability': false,
  //     'Close out a credit card': false,
  //     'Close out or transfer a retirement account and/or pension': false,
  //     'Designate the trust as beneficiary of a retirement account': false,
  //     'Close out or transfer an annuity': false,
  //     'Designate the trust as beneficiary of a bank/investment account': false,
  //     'Designate the trust as beneficiary of a life insurance policy': false,
  //     'Obtain Trust/Estate EIN': false,
  //     'Prepare document for grantor to sign that evidences the gift to the trust': false,
  //     // 'Close out or transfer a magazine subscription': false,
  //     // "Close out or transfer a utility account. (i.e., The decedent 's electric, gas, or water.)":
  //     //   false,
  //     // 'Close out or transfer a social media account': false,
  //     // 'Close out or transfer a bond': false,
  //     // 'Prepare assignment of business interest/etc (admin)': false,
  //     // 'Prepare IRS Form W-9 for the trust': false,
  //     // "If making §645 election, file IRS Form 8855 by the date the estate 's initial income tax return is filed (if it is on extension, filed by the extended return 's due date)":
  //     //   false,
  //   },
  // },
  {
    id: 'solo',
    name: 'Solo',
    isMostPopular: false,
    priceIdKey: FeatureSubscriptionProductCode.SoloPlan,
    features: {
      'Run Trusuite (real property, liability, and employment discovery) search': true,
      'Prepare a deed': true,
      'Draft estate planning documents': false,
      'Run bank account search - Top 10': true,
      'Run a life insurance discovery search': true,
      'NEW! Run a stock search - ComputerShare': true,
      'Notify credit bureaus and SSA of death': true,
      'Prepare IRS Form 56 and file it': true,
      'Close out or transfer a bank account': true,
      'Close out or transfer an investment account': true,
      'Close out or transfer outright stock': true,
      'Prepare assignment of business interest/etc (funding)': true,
      'Run a retirement account search': false,
      'NEW! Life Insurance Quick Search': false,
      'Notify employer and request unpaid benefits/survivor benefits': false,
      'Open estate/trust checking account': false,
      'Probate documents': false,
      'Obtain a deed for real property owned by the decedent': false,
      'Deed Tax Docs': false,
      'Search unclaimed property records nationwide': false,
      'Run bank account search - Private Wealth Management Firms': false,
      'Request life insurance Form 712': false,
      'NEW! Run Bank Search - Quick Results': false,
      'NEW! Run a stock search - EQ Shareowner Services - Quick Results': false,
      'Run bank account search - Regional Banks - West': false,
      'Run bank account search - Regional Banks - SE/SW': false,
      'Run bank account search - Regional Banks - NE/Mid Atlantic': false,
      'Run bank account search - Regional Banks - Midwest': false,
      'Obtain more information on a known or found account': false,
      'Request the date of death value for each asset and/or liability': false,
      'Close out a credit card': false,
      'Close out or transfer a retirement account and/or pension': false,
      'Designate the trust as beneficiary of a retirement account': false,
      'Close out or transfer an annuity': false,
      'Designate the trust as beneficiary of a bank/investment account': false,
      'Designate the trust as beneficiary of a life insurance policy': false,
      'Obtain Trust/Estate EIN': false,
      'Prepare document for grantor to sign that evidences the gift to the trust': false,
      // 'Close out or transfer a magazine subscription': false,
      // "Close out or transfer a utility account. (i.e., The decedent 's electric, gas, or water.)":
      //   false,
      // 'Close out or transfer a social media account': false,
      // 'Close out or transfer a bond': false,
      // 'Prepare assignment of business interest/etc (admin)': false,
      // 'Prepare IRS Form W-9 for the trust': false,
      // "If making §645 election, file IRS Form 8855 by the date the estate 's initial income tax return is filed (if it is on extension, filed by the extended return 's due date)":
      //   false,
    },
  },
  {
    id: 'smallFirm',
    name: 'Small Firm',
    isMostPopular: true,
    priceIdKey: FeatureSubscriptionProductCode.SmallFirmPlan,
    features: {
      'Run Trusuite (real property, liability, and employment discovery) search': true,
      'Prepare a deed': true,
      'Draft estate planning documents': true,
      'Run bank account search - Top 10': true,
      'Run a life insurance discovery search': true,
      'NEW! Run a stock search - ComputerShare': true,
      'Notify credit bureaus and SSA of death': true,
      'Prepare IRS Form 56 and file it': true,
      'Close out or transfer a bank account': true,
      'Close out or transfer an investment account': true,
      'Close out or transfer outright stock': true,
      'Prepare assignment of business interest/etc (funding)': true,
      'Run a retirement account search': true,
      'NEW! Life Insurance Quick Search': true,
      'Notify employer and request unpaid benefits/survivor benefits': true,
      'Open estate/trust checking account': true,
      'Probate documents': true,
      'Obtain a deed for real property owned by the decedent': true,
      'Deed Tax Docs': true,
      'Search unclaimed property records nationwide': false,
      'Run bank account search - Private Wealth Management Firms': true,
      'Request life insurance Form 712': true,
      'NEW! Run Bank Search - Quick Results': false,
      'NEW! Run a stock search - EQ Shareowner Services - Quick Results': false,
      'Run bank account search - Regional Banks - West': false,
      'Run bank account search - Regional Banks - SE/SW': false,
      'Run bank account search - Regional Banks - NE/Mid Atlantic': false,
      'Run bank account search - Regional Banks - Midwest': false,
      'Obtain more information on a known or found account': false,
      'Request the date of death value for each asset and/or liability': false,
      'Close out a credit card': false,
      'Close out or transfer a retirement account and/or pension': false,
      'Designate the trust as beneficiary of a retirement account': false,
      'Close out or transfer an annuity': false,
      'Designate the trust as beneficiary of a bank/investment account': false,
      'Designate the trust as beneficiary of a life insurance policy': false,
      'Obtain Trust/Estate EIN': false,
      'Prepare document for grantor to sign that evidences the gift to the trust': false,
      // 'Close out or transfer a magazine subscription': false,
      // "Close out or transfer a utility account. (i.e., The decedent 's electric, gas, or water.)":
      //   false,
      // 'Close out or transfer a social media account': false,
      // 'Close out or transfer a bond': false,
      // 'Prepare assignment of business interest/etc (admin)': false,
      // 'Prepare IRS Form W-9 for the trust': false,
      // "If making §645 election, file IRS Form 8855 by the date the estate 's initial income tax return is filed (if it is on extension, filed by the extended return 's due date)":
      //   false,
    },
  },
  {
    id: 'midLarqeFirm',
    name: 'Mid-Large Firm',
    isMostPopular: false,
    priceIdKey: FeatureSubscriptionProductCode.MidLargeFirmPlan,
    features: {
      'Run Trusuite (real property, liability, and employment discovery) search': true,
      'Prepare a deed': true,
      'Draft estate planning documents': true,
      'Run bank account search - Top 10': true,
      'Run a life insurance discovery search': true,
      'NEW! Run a stock search - ComputerShare': true,
      'Notify credit bureaus and SSA of death': true,
      'Prepare IRS Form 56 and file it': true,
      'Close out or transfer a bank account': true,
      'Close out or transfer an investment account': true,
      'Close out or transfer outright stock': true,
      'Prepare assignment of business interest/etc (funding)': true,
      'Run a retirement account search': true,
      'NEW! Life Insurance Quick Search': true,
      'Notify employer and request unpaid benefits/survivor benefits': true,
      'Open estate/trust checking account': true,
      'Probate documents': true,
      'Obtain a deed for real property owned by the decedent': true,
      'Deed Tax Docs': true,
      'Search unclaimed property records nationwide': true,
      'Run bank account search - Private Wealth Management Firms': true,
      'Request life insurance Form 712': true,
      'NEW! Run Bank Search - Quick Results': true,
      'NEW! Run a stock search - EQ Shareowner Services - Quick Results': true,
      'Run bank account search - Regional Banks - West': true,
      'Run bank account search - Regional Banks - SE/SW': true,
      'Run bank account search - Regional Banks - NE/Mid Atlantic': true,
      'Run bank account search - Regional Banks - Midwest': true,
      'Obtain more information on a known or found account': true,
      'Request the date of death value for each asset and/or liability': true,
      'Close out a credit card': true,
      'Close out or transfer a retirement account and/or pension': true,
      'Designate the trust as beneficiary of a retirement account': true,
      'Close out or transfer an annuity': true,
      'Designate the trust as beneficiary of a bank/investment account': true,
      'Designate the trust as beneficiary of a life insurance policy': true,
      'Obtain Trust/Estate EIN': true,
      'Prepare document for grantor to sign that evidences the gift to the trust': true,
      // 'Close out or transfer a magazine subscription': false,
      // "Close out or transfer a utility account. (i.e., The decedent 's electric, gas, or water.)":
      //   false,
      // 'Close out or transfer a social media account': false,
      // 'Close out or transfer a bond': false,
      // 'Prepare assignment of business interest/etc (admin)': false,
      // 'Prepare IRS Form W-9 for the trust': false,
      // "If making §645 election, file IRS Form 8855 by the date the estate 's initial income tax return is filed (if it is on extension, filed by the extended return 's due date)":
      //   false,
    },
  },
];

const SubscriptionPricingTablePage = () => {
  const classes = useStyles();
  const location = useLocation();
  const { portalId } = useParams<Record<string, string>>();
  const [payCycle, setSelectedPayCycle] = useState<FeatureSubscriptionPaymentCycle>(
    FeatureSubscriptionPaymentCycle.Yearly,
  );

  const { data: featureSubscriptionTypeData, loading: featureSubscriptionTypeLoading } =
    useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting feature subscription type: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  if (!featureSubscriptionTypeData && featureSubscriptionTypeLoading) {
    return <Spinner />;
  }

  if (
    featureSubscriptionTypeData!.data !== FeatureSubscriptionType.None &&
    process.env.REACT_APP_NODE_ENV === NodeEnvironment.PROD
  ) {
    return (
      <Redirect
        to={{
          pathname: '/',
          state: { from: location.pathname },
        }}
      />
    );
  }

  return (
    <HomeLayout>
      <div className={classes.container}>
        <Typography variant="h3" color="'#1C2541'">
          Choose a billing plan
        </Typography>
        <Typography variant="subtitle1">
          Whether you're just getting started with a couple of estates or scaling across your
          organization, we've got you covered.
        </Typography>
        <div className={classes.switchContainer}>
          <LabeledSwitch
            selectedOption={payCycle}
            setSelectedOption={setSelectedPayCycle}
            leftLabel="ANNUAL BILLING"
            rightLabel="MONTHLY BILLING"
          />
        </div>
        <PricingTable plans={plans} payCycle={payCycle} />
        <div className={classes.pricingTableFooterContainer}>
          <PricingTableFooter />
        </div>
      </div>
    </HomeLayout>
  );
};

export default SubscriptionPricingTablePage;
